import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Select, Tag, Input, Spin, Empty, message, Tabs, Table, Tooltip } from 'antd';
import { LoadingOutlined, SearchOutlined, TagOutlined, LineChartOutlined, CloseOutlined, HistoryOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
    GET_SUBTASK_KII_RELATION,
    SAVE_SUBTASK_TAGGING,
    Get_LeadIndicatorFunnel,
    GET_SUBTASK_RELATION_EVENTS,
    ADD_UPDATE_KII_SUBTASK
} from '../../../../services/userAPI';
import dayjs from 'dayjs';

const { TabPane } = Tabs;

const SubtaskTaggingModal = ({
    visible,
    onCancel,
    subtask,
    roleId,
    onTaggingSaved
}) => {
    // State for selected items
    const [selectedLeadIndicatorIds, setSelectedLeadIndicatorIds] = useState([]);

    // Search states
    const [leadIndicatorSearch, setLeadIndicatorSearch] = useState('');

    // Saving state
    const [isSaving, setIsSaving] = useState(false);

    // Active tab
    const [activeTab, setActiveTab] = useState('tagging');

    // Fetch existing tagging for this subtask using new API
    const { data: taggingData, loading: taggingLoading, refetch: refetchTagging } = useQuery(GET_SUBTASK_KII_RELATION, {
        variables: { subtaskId: subtask?.id },
        skip: !visible || !subtask?.id,
        fetchPolicy: 'network-only',
    });

    // Fetch all lead indicators for the role
    const { data: leadIndicatorsData, loading: leadIndicatorsLoading } = useQuery(Get_LeadIndicatorFunnel, {
        variables: { roleId: parseInt(roleId) },
        skip: !visible || !roleId,
    });

    // Audit log pagination state
    const [auditPage, setAuditPage] = useState(1);
    const [auditPageSize, setAuditPageSize] = useState(10);

    // Fetch audit log with pagination
    const [fetchAuditLog, { data: auditData, loading: auditLoading }] = useLazyQuery(GET_SUBTASK_RELATION_EVENTS, {
        fetchPolicy: 'network-only',
    });

    // Save tagging mutation
    const [saveTagging] = useMutation(SAVE_SUBTASK_TAGGING);

    // Add/Update KII Subtask mutation
    const [addUpdateKiiSubtask] = useMutation(ADD_UPDATE_KII_SUBTASK);

    // Get lead indicators list
    const leadIndicators = useMemo(() => {
        return leadIndicatorsData?.getKiisForRole || [];
    }, [leadIndicatorsData]);

    // Filter lead indicators by search
    const filteredLeadIndicators = useMemo(() => {
        if (!leadIndicatorSearch) return leadIndicators;
        return leadIndicators.filter(li =>
            li.name?.toLowerCase().includes(leadIndicatorSearch.toLowerCase())
        );
    }, [leadIndicators, leadIndicatorSearch]);

    // Initialize selected items from existing tagging
    useEffect(() => {
        if (taggingData?.getSubtaskKiiRelation) {
            const relations = taggingData.getSubtaskKiiRelation;
            setSelectedLeadIndicatorIds(relations?.map(r => r.kiiId) || []);
        } else {
            setSelectedLeadIndicatorIds([]);
        }
    }, [taggingData]);

    // Fetch audit log when tab changes or pagination changes
    useEffect(() => {
        if (activeTab === 'audit' && subtask?.id) {
            fetchAuditLog({
                variables: {
                    subtaskId: subtask.id,
                    pagingCriteria: {
                        limit: auditPageSize,
                        page: auditPage,
                        sortByField: "created_at",
                        sortOrder: "desc"
                    }
                }
            });
        }
    }, [activeTab, subtask?.id, fetchAuditLog, auditPage, auditPageSize]);

    // Reset state when modal closes
    useEffect(() => {
        if (!visible) {
            setLeadIndicatorSearch('');
            setActiveTab('tagging');
            setAuditPage(1);
        }
    }, [visible]);

    // Handle lead indicator selection
    const handleLeadIndicatorSelect = (id) => {
        setSelectedLeadIndicatorIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    // Remove lead indicator
    const removeLeadIndicator = (id) => {
        setSelectedLeadIndicatorIds(prev => prev.filter(i => i !== id));
    };

    // Handle save
    const handleSave = async () => {
        if (!subtask?.id) return;

        setIsSaving(true);
        try {
            // Get existing tagged lead indicator IDs
            const existingIds = taggingData?.getSubtaskKiiRelation?.map(r => r.kiiId) || [];

            // Find IDs to add (in selected but not in existing)
            const idsToAdd = selectedLeadIndicatorIds.filter(id => !existingIds.includes(id));

            // Find IDs to remove (in existing but not in selected)
            const idsToRemove = existingIds.filter(id => !selectedLeadIndicatorIds.includes(id));

            // Call addUpdateKiiSubtask for each change
            const promises = [];

            // Add new relations
            for (const kiiId of idsToAdd) {
                promises.push(
                    addUpdateKiiSubtask({
                        variables: {
                            subtaskId: subtask.id,
                            kiiId: kiiId,
                            removeRelation: false
                        }
                    })
                );
            }

            // Remove relations
            for (const kiiId of idsToRemove) {
                promises.push(
                    addUpdateKiiSubtask({
                        variables: {
                            subtaskId: subtask.id,
                            kiiId: kiiId,
                            removeRelation: true
                        }
                    })
                );
            }

            // Wait for all mutations to complete
            const results = await Promise.all(promises);

            // Check if all succeeded
            const allSucceeded = results.every(r => r.data?.addUpdateKiiSubtask?.success);

            if (allSucceeded || promises.length === 0) {
                // message.success('Tagging saved successfully!');
                refetchTagging();
                if (onTaggingSaved) {
                    onTaggingSaved({
                        subtaskId: subtask.id,
                        leadIndicatorCount: selectedLeadIndicatorIds.length,
                        videoCount: 0,
                    });
                }
                onCancel();
            } else {
                message.error('Some tagging operations failed');
            }
        } catch (error) {
            console.error('Error saving tagging:', error);
            message.error('Failed to save tagging. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    // Get selected lead indicator objects
    const selectedLeadIndicators = useMemo(() => {
        return leadIndicators.filter(li => selectedLeadIndicatorIds.includes(li.id));
    }, [leadIndicators, selectedLeadIndicatorIds]);

    // Get KII name by ID
    const getKiiName = (kiiId) => {
        const kii = leadIndicators.find(li => li.id === kiiId);
        return kii?.name || `KII #${kiiId}`;
    };

    // Audit log columns
    const auditColumns = [
        {
            title: 'Action',
            dataIndex: 'actionType',
            key: 'actionType',
            render: (actionType) => (
                <Tag color={actionType === 'CREATED' ? 'green' : '#AD0101'}>
                    {actionType}
                </Tag>
            ),
        },
        // {
        //     title: 'Type',
        //     dataIndex: 'kiiId',
        //     key: 'type',
        //     render: () => (
        //         <span>
        //             Lead Indicator
        //         </span>
        //     ),
        // },
        {
            title: 'Lead Indicator',
            dataIndex: 'kiiName',
            key: 'kiiName',
        },
        {
            title: 'Action By',
            dataIndex: 'createdBy',
            key: 'createdBy',
            render: (createdBy) => createdBy?.name || '',
        },
        {
            title: 'Date & Time',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => dayjs(date).format('DD MMM YYYY, hh:mm A'),
        },
    ];

    // Handle audit pagination change
    const handleAuditPaginationChange = (page, pageSize) => {
        setAuditPage(page);
        setAuditPageSize(pageSize);
    };

    const isLoading = taggingLoading || leadIndicatorsLoading;

    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            title={
                <div className="flex items-center font-semibold text-base">
                    <TagOutlined className="mr-2" />
                    Tag Lead Indicators
                    {subtask && (
                        <span className="ml-3 px-3 py-1 bg-gray-100 rounded text-sm font-medium text-gray-600">
                            {subtask.name}
                        </span>
                    )}
                </div>
            }
            width={900}
            style={{ top: 20 }}
            footer={
                activeTab === 'tagging' ? (
                    <div className="flex justify-end gap-3">
                        <button
                            className="px-5 py-2 border border-gray-300 rounded bg-white text-gray-700 text-sm cursor-pointer transition-all hover:border-[#AD0101] hover:text-[#AD0101]"
                            onClick={onCancel}
                        >
                            Cancel
                        </button>
                        <button
                            className={`px-6 py-2 border-none rounded text-sm cursor-pointer transition-all ${isSaving
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-red-600 text-white hover:bg-red-700'
                                }`}
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : (taggingData?.getSubtaskKiiRelation?.length > 0 ? 'Update' : 'Save')}
                        </button>
                    </div>
                ) : null
            }
        >
            <Spin
                spinning={isLoading || isSaving}
                indicator={<LoadingOutlined style={{ fontSize: 24, color: '#AD0101' }} spin />}
            >
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane
                        tab={<span><TagOutlined /> Tagging</span>}
                        key="tagging"
                    >
                        <div className="min-h-[400px]">
                            {/* Lead Indicators Section */}
                            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                <div className="flex items-center mb-3 pb-3 border-b border-gray-200">
                                    <LineChartOutlined className="text-lg text-red-600 mr-2" />
                                    <span className="font-semibold text-sm flex-1">Lead Indicators</span>
                                    <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">
                                        {selectedLeadIndicatorIds.length} selected
                                    </span>
                                </div>

                                {/* Selected Lead Indicators Tags */}
                                {selectedLeadIndicators.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-3 p-2 bg-white rounded border border-dashed border-gray-300 min-h-[40px]">
                                        {selectedLeadIndicators.map(li => (
                                            <Tag
                                                key={li.id}
                                                closable
                                                onClose={() => removeLeadIndicator(li.id)}
                                                style={{
                                                    backgroundColor: li.colorValue || '#f0f0f0',
                                                    borderColor: li.colorValue || '#d9d9d9',
                                                    color: '#333',
                                                    margin: 0
                                                }}
                                            >
                                                {li.name}
                                            </Tag>
                                        ))}
                                    </div>
                                )}

                                {/* Search and Select Lead Indicators */}
                                <Input
                                    placeholder="Search lead indicators..."
                                    prefix={<SearchOutlined />}
                                    value={leadIndicatorSearch}
                                    onChange={(e) => setLeadIndicatorSearch(e.target.value)}
                                    className="mb-3"
                                />

                                <div className="max-h-[250px] overflow-y-auto bg-white rounded border border-gray-200">
                                    {filteredLeadIndicators.length === 0 ? (
                                        <Empty
                                            description="No lead indicators found"
                                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                                            className="py-6"
                                        />
                                    ) : (
                                        filteredLeadIndicators.map(li => (
                                            <div
                                                key={li.id}
                                                className={`flex items-center px-3 py-2.5 cursor-pointer border-b border-gray-100 last:border-b-0 transition-all hover:bg-gray-50 ${selectedLeadIndicatorIds.includes(li.id)
                                                        ? 'bg-red-50 border-l-[3px] border-l-red-600'
                                                        : ''
                                                    }`}
                                                onClick={() => handleLeadIndicatorSelect(li.id)}
                                            >
                                                <div
                                                    className="w-3 h-3 rounded-full mr-2.5 flex-shrink-0"
                                                    style={{ backgroundColor: li.colorValue || '#ccc' }}
                                                />
                                                <span className="flex-1 text-sm font-medium">{li.name}</span>
                                                {/* <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                                    Target: {li.dailyTarget || li.defaultValue || '-'}
                                                </span> */}
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </TabPane>

                    <TabPane
                        tab={<span><HistoryOutlined /> Audit Log</span>}
                        key="audit"
                    >
                        <Spin
                            spinning={auditLoading}
                            indicator={<LoadingOutlined style={{ fontSize: 24, color: '#AD0101' }} spin />}
                        >
                            <Table
                                columns={auditColumns}
                                dataSource={auditData?.getSubtaskRelationEvents?.data || []}
                                rowKey="id"
                                pagination={{
                                    current: auditPage,
                                    pageSize: auditPageSize,
                                    total: auditData?.getSubtaskRelationEvents?.pageMeta?.totalCount || 0,
                                    onChange: handleAuditPaginationChange,
                                    showSizeChanger: false,
                                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
                                }}
                                locale={{ emptyText: 'No audit records found' }}
                                className="mt-4"
                                size="small"
                            />
                        </Spin>
                    </TabPane>
                </Tabs>
            </Spin>
        </Modal>
    );
};

export default SubtaskTaggingModal;
